/**
 * MediaResourceFormV2 — V2 port of the Media Resource create form
 * (no edit route exists in v1, so this is create-only).
 *
 * Sections:
 *   01 Resource info — name (req) + description (req)
 *   02 File          — UploadTile for the resource artwork
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { saveMediaResource } from '../../../../actions/mediaResourceActions';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';

import { Button, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  UploadTile,
} from '../../../redesign/edit';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function MediaResourceFormV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((s) => s.mediaResources?.loading);

  const [mediaResource, setMediaResource] = useState({
    name: '',
    description: '',
    file: null,
  });

  const handleChange = (e) => {
    setMediaResource((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaResource((prev) => ({ ...prev, file }));
  };

  const handleRemove = () => {
    setMediaResource((prev) => ({ ...prev, file: null }));
  };

  const handleSave = (e) => {
    if (e?.preventDefault) e.preventDefault();
    dispatch(saveMediaResource(mediaResource, () => navigate('/media-resources')));
  };

  // For UploadTile previewing the freshly-selected File, build the
  // blob URL on render — cheap, browser GCs it when the form
  // unmounts. (We don't navigate away on save until the dispatch's
  // onSuccess fires, so leaks aren't a concern here.)
  const previewUrl = mediaResource.file
    ? (typeof mediaResource.file === 'string'
        ? mediaResource.file
        : URL.createObjectURL(mediaResource.file))
    : '';

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              New media resource
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !mediaResource.name && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={mediaResource.name || 'Untitled media resource'}
            >
              {mediaResource.name || 'Untitled media resource'}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              Upload a small image asset (icon / glyph) for use across
              the app — referenced by name from card forms etc.
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/media-resources" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="media-resource-form"
              leadingIcon={SaveIconSvg}
            >
              Create
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <form
          id="media-resource-form"
          onSubmit={handleSave}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0, maxWidth: 880 }}
        >
          {/* 01 Resource info */}
          <FormSection title="Resource info" num="01">
            <FieldRow cols={2} minColWidth={240}>
              <FieldBlock label="Name" required htmlFor="f-mr-name">
                <TextInput
                  id="f-mr-name"
                  name="name"
                  value={mediaResource.name}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </FieldBlock>
              <FieldBlock label="Description" required htmlFor="f-mr-desc">
                <Textarea
                  id="f-mr-desc"
                  name="description"
                  value={mediaResource.description}
                  onChange={handleChange}
                  rows={3}
                  required
                  placeholder="What is this resource and where is it used?"
                />
              </FieldBlock>
            </FieldRow>
          </FormSection>

          {/* 02 File */}
          <FormSection title="File" num="02">
            <UploadTile
              label={mediaResource.file?.name || 'Resource file'}
              hint="PNG / SVG / JPG"
              value={previewUrl}
              accept="image/*"
              onChange={handleImageUpload}
              onRemove={handleRemove}
            />
          </FormSection>
        </form>
      </div>
    </div>
  );
}
